"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Natalia\xA0\nFerian",
  role: "CEO Neogourmet & Neoparklet",
  company: "Neogourmet",
  country: "São Paulo",
  quote: "A equipe da ITC+ trouxe segurança e profissionalismo para o nosso projeto. Foi um suporte essencial para estruturar nossa presença digital com mais confiança.",
  avatar: "https://itcmais.com/wp-content/uploads/2026/04/Logo-NEOS-scaled.png?v=aqc0efpp8z5",
  initials: "RA"
}, {
  id: 2,
  name: "Sun\nConde",
  role: "CEO,\nSoham.Pausa",
  company: "Soham.Pausa",
  country: "Faria Lima",
  quote: "A ITC+ tem um olhar estratégico muito claro. Além da execução, oferecem direcionamento e apoio real no desenvolvimento dos nossos projetos.",
  avatar: "https://itcmais.com/wp-content/uploads/2026/04/soham-cast.jpg?v=46dyhd3gzd",
  initials: "JM"
}, {
  id: 3,
  name: "Douglas\nNogueira",
  role: "Fundador,\nHBS",
  company: "HBS",
  country: "São Paulo",
  quote: "Empresa de excelência. A ITC+ entrega com qualidade, organização e um padrão profissional acima do mercado.",
  avatar: "https://itcmais.com/wp-content/uploads/2026/04/Douglas.png?v=56imh2ix4h5",
  initials: "FS"
}, {
  id: 4,
  name: "Beatriz Costa",
  role: "Head de Growth",
  company: "Financier Hub",
  country: "Pinheiros",
  quote: "A estrutura em Pinheiros é impecável. Gravamos nossa série de vídeos estratégicos com agilidade e uma qualidade de imagem que impressionou nossos investidores.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645251/img/depoimento_beatriz_costa.png",
  initials: "BC"
}, {
  id: 5,
  name: "André Luiz",
  role: "Gerente de Comunicação",
  company: "Logos Logística",
  country: "São Paulo",
  quote: "Trabalhar com a ITC+ é garantia de profissionalismo. Desde o roteiro até a edição final, o foco em conversão e SEO local é perceptível em cada detalhe do projeto.",
  avatar: "https://itcmais.com/wp-content/uploads/2026/04/Captura-de-tela-2026-04-30-113733-e1777559903906.png?v=cx5qr1xsywk",
  initials: "AL"
}];
export default function ClientTestimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [prevHovered, setPrevHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  return <section data-section-id="231718" id="empresas-que-já-confiaram-na-itc" ref={sectionRef} className="py-20 md:py-28 bg-background text-foreground">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {}
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs font-semibold tracking-widest uppercase mb-4 font-mono text-primary/60" style={{
          letterSpacing: "0.18em"
        }}>
            Cases de Sucesso
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight" style={{
          letterSpacing: "-0.02em"
        }}>
            Empresas que já confiaram na ITC+
          </h2>
          <p className="mt-4 text-base md:text-lg font-default max-w-xl mx-auto text-muted-foreground">
            Projetos para empresas que buscam posicionamento e crescimento através do audiovisual estratégico.
          </p>
        </div>

        {}
        <div className={`relative transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-6">
              {testimonials.map((t, i) => <CarouselItem data-index={i} key={t.id} className="pl-6 basis-full md:basis-1/2 lg:basis-1/3">
                  <div className="relative bg-card rounded-2xl p-7 h-full flex flex-col border border-border shadow-sm">
                    {}
                    <div className="mb-5">
                      <Quote className="size-8 text-primary opacity-20" strokeWidth={2.5} />
                    </div>

                    {}
                    <p className="font-serif italic flex-1 leading-relaxed mb-7 text-foreground/90" style={{
                  fontSize: "18px",
                  fontWeight: 400,
                  lineHeight: "1.65"
                }}>
                      &ldquo;{t.quote}&rdquo;
                    </p>

                    {}
                    <div className="flex items-center justify-between">
                      {}
                      <div className="flex items-center gap-3">
                        <SafeHydrate><Avatar className="size-12 shrink-0 border border-border">
                          <AvatarImage src={t.avatar} alt={t.name} />
                          <AvatarFallback className="text-sm font-semibold text-primary-foreground bg-primary">
                            {t.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <p className="font-default font-bold leading-tight text-foreground" style={{
                        fontSize: "15px"
                      }}>
                            {t.name}
                          </p>
                          <p className="font-default text-xs mt-0.5 text-muted-foreground">
                            {t.role}, {t.country}
                          </p>
                        </div>
                      </div>

                      {}
                      <div className="shrink-0 ml-3 px-2.5 py-1 rounded-full bg-primary/5 border border-primary/10">
                        <span className="font-default font-semibold text-xs whitespace-nowrap text-primary" style={{
                      fontSize: "11px",
                      letterSpacing: "0.02em"
                    }}>
                          {t.company}
                        </span>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="flex items-center justify-center gap-5 mt-10">
            {}
            <Button variant="outline" size="icon" onClick={scrollPrev} onMouseEnter={() => setPrevHovered(true)} onMouseLeave={() => setPrevHovered(false)} className="size-12 rounded-full border-2 transition-all duration-200 border-primary/30 text-primary hover:bg-primary hover:text-white hover:border-primary" style={{
            transform: prevHovered ? "scale(1.08)" : "scale(1)"
          }} aria-label="Previous testimonial">
              <ChevronLeft className="size-5" />
            </Button>

            {}
            <div className="flex items-center gap-2">
              {Array.from({
              length: count
            }).map((_, idx) => <Button data-index={idx} key={idx} variant="ghost" size="sm" onClick={() => api?.scrollTo(idx)} className="rounded-full transition-all duration-300 p-0 focus:outline-none hover:bg-transparent" style={{
              width: current === idx ? "24px" : "8px",
              height: "8px",
              backgroundColor: current === idx ? "hsl(270, 70%, 45%)" : "rgba(270, 70%, 45%, 0.2)"
            }} aria-label={`Go to slide ${idx + 1}`} />)}
            </div>

            {}
            <Button variant="outline" size="icon" onClick={scrollNext} onMouseEnter={() => setNextHovered(true)} onMouseLeave={() => setNextHovered(false)} className="size-12 rounded-full border-2 transition-all duration-200 border-primary/30 text-primary hover:bg-primary hover:text-white hover:border-primary" style={{
            transform: nextHovered ? "scale(1.08)" : "scale(1)"
          }} aria-label="Next testimonial">
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}